import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { IEquipment } from '~/utils/interface/equipment';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, handleApiError, notify } from '~/utils/helper';
import { BaseRow, BaseSelect, BaseUploadFile } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { UploadFile } from '~/utils/types/common';

import { handleCreateEquipment, handleEditEquipment } from '~/thunks/equipment/equipmentThunk';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { equipmentActions } from '~/thunks/equipment/equipmentSlice';
import { EQUIPMENT_TYPES } from '~/utils/constants/equipment';

type EditEquipmentDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  setOpenDrawer?: (open: boolean) => void;
  mode?: string;
  title?: string;
  data?: IEquipment;
  children?: React.ReactNode;
};

const initialValues: IEquipment = {
  name: '',
  type: '',
  image: undefined,
};

const EditEquipmentDialog = (props: EditEquipmentDialogProps) => {
  const { mode = DIALOG_MODE_CREATE, data } = props;

  const { t } = useTranslation(['admin/equipment']);
  const dispatch = useAppDispatch();
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      if (mode === DIALOG_MODE_CREATE) {
        try {
          await dispatch(handleCreateEquipment(values)).unwrap();
          dispatch(equipmentActions.changeNeedRefreshData(true));
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          await dispatch(handleEditEquipment({ id: equipmentId, params: values })).unwrap();
          dispatch(equipmentActions.changeNeedRefreshData(true));
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false, true);
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  const { equipmentId } = useAppSelector((state) => state.equipment);

  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT) {
      form.setFieldValue('name', cast(data?.name));
      form.setFieldValue('type', cast(data?.type));
      form.setFieldValue('image', data?.image);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, data]);

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    if (props.setOpenDrawer) {
      props.setOpenDrawer(open);
    }
  };

  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
  };

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addNew') : t('edit')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.name')}</InputLabel>
              <TextField
                name='name'
                placeholder={t('properties.name')}
                value={form.values?.name}
                onChange={form.handleChange}
                error={Boolean(form.errors?.name)}
                helperText={form.errors?.name}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px] mt-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('properties.type')}</InputLabel>
              <BaseSelect
                options={EQUIPMENT_TYPES}
                name='type'
                isHideIconCheckbox={true}
                value={cast(form.values?.type)}
                onChange={(e) => form.setFieldValue('type', e.target.value)}
                error={Boolean(form.errors?.type)}
                helperText={form.errors?.type}
              />
            </Box>
          </BaseRow>
          <Box className='gap-[20px] mb-[20px]'>
            <InputLabel>{t('properties.image')}</InputLabel>
            <BaseUploadFile
              uploadedFiles={typeof form.values?.image === 'string' ? [{ url: form.values?.image }] : undefined}
              onChange={(files: Array<UploadFile>) => {
                form.setFieldValue('image', files[0]?.file ?? '');
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditEquipmentDialog);
